import { EntityType, ViewerGraphData, ViewerNode } from '../models/graph-viewer.models';

export interface GraphMapMarker {
  id: string;
  lat: number;
  lng: number;
  title: string;
  subtitle: string;
  sourceKind: 'coordinates' | 'map_data' | 'geocoded';
  nodeIds: string[];
  primaryNodeId: string;
  entityType: EntityType;
}

const geocodeCache = new Map<string, { lat: number; lng: number } | null>();
const MAX_GEOCODE_REQUESTS = 20;
const GEOCODE_DELAY_MS = 1100;

const KNOWN_LOCATION_COORDS: Record<string, { lat: number; lng: number }> = {
  'delhi, india': { lat: 28.6139, lng: 77.209 },
  'new delhi, india': { lat: 28.6139, lng: 77.209 },
  'gurgaon, haryana, india': { lat: 28.4595, lng: 77.0266 },
  'gurgaon, india': { lat: 28.4595, lng: 77.0266 },
  'noida, india': { lat: 28.5355, lng: 77.391 },
  'noida, uttar pradesh, india': { lat: 28.5355, lng: 77.391 },
  'kalkaji, delhi, india': { lat: 28.5494, lng: 77.2571 },
};

function isValidCoord(lat: number, lng: number): boolean {
  return Number.isFinite(lat) && Number.isFinite(lng) && Math.abs(lat) <= 90 && Math.abs(lng) <= 180;
}

function coordKey(lat: number, lng: number): string {
  return `${lat.toFixed(5)},${lng.toFixed(5)}`;
}

function pushCoord(
  markers: GraphMapMarker[],
  seen: Set<string>,
  node: ViewerNode,
  lat: number,
  lng: number,
  subtitle: string,
  sourceKind: GraphMapMarker['sourceKind'],
): void {
  if (!isValidCoord(lat, lng)) return;
  const key = coordKey(lat, lng);
  const existing = markers.find((m) => coordKey(m.lat, m.lng) === key);
  if (existing) {
    if (!existing.nodeIds.includes(node.id)) existing.nodeIds.push(node.id);
    return;
  }
  if (seen.has(`${key}:${node.id}`)) return;
  seen.add(`${key}:${node.id}`);
  markers.push({
    id: `map:${key}:${node.id}`,
    lat,
    lng,
    title: node.label,
    subtitle,
    sourceKind,
    nodeIds: [node.id],
    primaryNodeId: node.id,
    entityType: node.type,
  });
}

function findLatLngPairs(value: unknown, out: Array<{ lat: number; lng: number; label?: string }>): void {
  if (value == null) return;
  if (Array.isArray(value)) {
    if (
      value.length >= 2 &&
      typeof value[0] === 'number' &&
      typeof value[1] === 'number' &&
      isValidCoord(value[0], value[1])
    ) {
      out.push({ lat: value[0], lng: value[1] });
      return;
    }
    value.forEach((item) => findLatLngPairs(item, out));
    return;
  }
  if (typeof value !== 'object') return;
  const obj = value as Record<string, unknown>;
  if (Array.isArray(obj['lat_lng']) && obj['lat_lng'].length >= 2) {
    const lat = Number(obj['lat_lng'][0]);
    const lng = Number(obj['lat_lng'][1]);
    if (isValidCoord(lat, lng)) {
      out.push({
        lat,
        lng,
        label:
          typeof obj['title'] === 'string'
            ? obj['title']
            : typeof obj['address'] === 'string'
              ? obj['address']
              : undefined,
      });
    }
  }
  if (obj['lat'] != null && obj['lng'] != null) {
    const lat = Number(obj['lat']);
    const lng = Number(obj['lng']);
    if (isValidCoord(lat, lng)) out.push({ lat, lng });
  }
  Object.values(obj).forEach((child) => findLatLngPairs(child, out));
}

function collectLocationStrings(node: ViewerNode): string[] {
  const raw = node.raw || {};
  const metadata = raw.metadata || {};
  const strings = new Set<string>();

  const push = (value: unknown) => {
    if (typeof value !== 'string') return;
    const trimmed = value.trim();
    if (trimmed && trimmed.toLowerCase() !== 'null') strings.add(trimmed);
  };

  const pushList = (value: unknown) => {
    if (!Array.isArray(value)) return;
    value.forEach(push);
  };

  pushList(metadata.locations);
  if (node.type === 'location') push(node.label);

  const leakData = metadata.leakData?.leak_data || metadata.sourceData?.leak_data;
  if (Array.isArray(leakData)) {
    leakData.forEach((row: Record<string, unknown>) => {
      push(row['address']);
      push(row['address2']);
      push(row['address3']);
      push(row['city']);
      if (typeof row['name'] === 'string' && row['address']) {
        push(`${row['address']}${row['city'] ? `, ${row['city']}` : ''}`);
      }
    });
  }

  const fields = metadata.sourceData?.fields;
  if (fields && typeof fields === 'object') {
    const mapField = (fields as Record<string, unknown>)['Map Data'] || (fields as Record<string, unknown>)['map'];
    if (mapField && typeof mapField === 'object') {
      const value = (mapField as Record<string, unknown>)['value'];
      if (Array.isArray(value)) {
        value.forEach((entry) => {
          const popup = (entry as Record<string, unknown>)?.['popup'];
          if (popup && typeof popup === 'object') {
            push((popup as Record<string, unknown>)['address'] as string);
            push((popup as Record<string, unknown>)['title'] as string);
          }
        });
      }
    }
  }

  return Array.from(strings);
}

export function normalizeLocationQuery(location: string): string {
  const trimmed = location.trim();
  if (!trimmed) return '';

  if (/^raw:/i.test(trimmed)) {
    return trimmed.replace(/^raw:\s*/i, '').trim();
  }

  const parts: Record<string, string> = {};
  trimmed.split(/\s+(?=[A-Za-z_]+:)/).forEach((segment) => {
    const match = segment.match(/^([^:]+):\s*(.+)$/);
    if (match) parts[match[1].toLowerCase().replace(/\s+/g, '_')] = match[2].trim();
  });

  if (Object.keys(parts).length > 0) {
    const country =
      parts['country']?.toUpperCase() === 'IN' ? 'India' : parts['country'] || parts['country_code'];
    const pieces = [parts['city'], parts['state'], country, parts['zip_code']].filter(Boolean);
    if (pieces.length) return pieces.join(', ');
  }

  return trimmed;
}

function collectSyncMarkers(graphData: ViewerGraphData): GraphMapMarker[] {
  const markers: GraphMapMarker[] = [];
  const seen = new Set<string>();

  graphData.nodes.forEach((node) => {
    if (node.coords) {
      pushCoord(markers, seen, node, node.coords[0], node.coords[1], 'Stored coordinates', 'coordinates');
    }

    const raw = node.raw || {};
    const metadata = raw.metadata || {};
    if (metadata.lat != null && metadata.lng != null) {
      pushCoord(
        markers,
        seen,
        node,
        Number(metadata.lat),
        Number(metadata.lng),
        'Metadata coordinates',
        'coordinates',
      );
    }

    const latLngPairs: Array<{ lat: number; lng: number; label?: string }> = [];
    findLatLngPairs(metadata.sourceData, latLngPairs);
    findLatLngPairs(metadata, latLngPairs);
    latLngPairs.forEach((pair) => {
      pushCoord(markers, seen, node, pair.lat, pair.lng, pair.label || 'Map pin from source data', 'map_data');
    });
  });

  return markers;
}

async function geocodeLocation(query: string): Promise<{ lat: number; lng: number } | null> {
  const normalized = normalizeLocationQuery(query);
  if (!normalized || normalized.length < 3) return null;
  const cacheKey = normalized.toLowerCase();
  if (geocodeCache.has(cacheKey)) return geocodeCache.get(cacheKey) ?? null;

  const known = KNOWN_LOCATION_COORDS[cacheKey];
  if (known) {
    geocodeCache.set(cacheKey, known);
    return known;
  }

  try {
    const url = `https://nominatim.openstreetmap.org/search?format=json&limit=1&q=${encodeURIComponent(normalized)}`;
    const response = await fetch(url, {
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) {
      geocodeCache.set(cacheKey, null);
      return null;
    }
    const results = (await response.json()) as Array<{ lat: string; lon: string }>;
    const hit = results[0];
    if (!hit) {
      geocodeCache.set(cacheKey, null);
      return null;
    }
    const coords = { lat: Number(hit.lat), lng: Number(hit.lon) };
    if (!isValidCoord(coords.lat, coords.lng)) {
      geocodeCache.set(cacheKey, null);
      return null;
    }
    geocodeCache.set(cacheKey, coords);
    return coords;
  } catch {
    geocodeCache.set(cacheKey, null);
    return null;
  }
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function collectGeocodedMarkers(
  graphData: ViewerGraphData,
  existing: GraphMapMarker[],
): Promise<GraphMapMarker[]> {
  const markers: GraphMapMarker[] = [];
  const seenCoords = new Set(existing.map((m) => coordKey(m.lat, m.lng)));
  const queries = new Map<string, { nodes: ViewerNode[]; original: string }>();

  graphData.nodes.forEach((node) => {
    collectLocationStrings(node).forEach((location) => {
      const query = normalizeLocationQuery(location);
      if (!query) return;
      const key = query.toLowerCase();
      const entry = queries.get(key);
      if (entry) {
        if (!entry.nodes.some((n) => n.id === node.id)) entry.nodes.push(node);
      } else {
        queries.set(key, { nodes: [node], original: location });
      }
    });
  });

  let requestCount = 0;
  for (const [queryKey, { nodes, original }] of queries) {
    if (requestCount >= MAX_GEOCODE_REQUESTS) break;
    const coords = await geocodeLocation(queryKey);
    requestCount += 1;
    if (requestCount < MAX_GEOCODE_REQUESTS) await delay(GEOCODE_DELAY_MS);
    if (!coords) continue;

    const key = coordKey(coords.lat, coords.lng);
    const allMarkers = [...existing, ...markers];
    const existingMarker = allMarkers.find((m) => coordKey(m.lat, m.lng) === key);
    if (existingMarker) {
      nodes.forEach((node) => {
        if (!existingMarker.nodeIds.includes(node.id)) existingMarker.nodeIds.push(node.id);
      });
      continue;
    }
    if (seenCoords.has(key)) continue;
    seenCoords.add(key);

    const primary = nodes[0];
    markers.push({
      id: `geo:${key}:${primary.id}`,
      lat: coords.lat,
      lng: coords.lng,
      title: nodes.length > 1 ? original : primary.label,
      subtitle: nodes.length > 1 ? `${nodes.length} linked entities` : original,
      sourceKind: 'geocoded',
      nodeIds: nodes.map((n) => n.id),
      primaryNodeId: primary.id,
      entityType: primary.type === 'location' ? 'location' : primary.type,
    });
  }

  return markers;
}

export async function collectGraphMapMarkers(graphData: ViewerGraphData): Promise<GraphMapMarker[]> {
  const syncMarkers = collectSyncMarkers(graphData);
  const geocodedMarkers = await collectGeocodedMarkers(graphData, syncMarkers);
  return [...syncMarkers, ...geocodedMarkers];
}
